import { Navigate, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { PlusCircle, TrendingUp, Clock, CheckCircle, AlertTriangle, XCircle, Info, Eye, ChevronRight, BarChart3 } from 'lucide-react'
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts'
import { assessmentsApi, usersApi } from '@/lib/api'
import { useAuthStore } from '@/store/auth'
import { useEntitlements } from '@/hooks/use-entitlements'
import { QuotaBanner } from '@/components/QuotaBanner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { cn, verdictBadgeColor, verdictLabel, statusLabel, appCategoryLabel, formatDate } from '@/lib/utils'
import type { AssessmentListItem } from '@/types'

function StatCard({
  title, value, icon: Icon, color,
}: { title: string; value: number; icon: React.ElementType; color: string }) {
  return (
    <Card className={cn(
      'border-l-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md',
      color.replace('bg-', 'border-l-')
    )}>
      <CardContent className="flex items-center gap-4 pt-6">
        <div className={`w-12 h-12 rounded-md flex items-center justify-center shadow-md shadow-black/10 ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div>
          <p className="text-2xl font-bold tabular-nums">{value}</p>
          <p className="text-sm text-muted-foreground">{title}</p>
        </div>
      </CardContent>
    </Card>
  )
}

const MONTH_LABELS_IT = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic']
const MONTH_LABELS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function monthLabel(ym: string, lang: string): string {
  const m = parseInt(ym.slice(5), 10) - 1
  const labels = lang.startsWith('it') ? MONTH_LABELS_IT : MONTH_LABELS_EN
  return labels[m] ?? ym
}

function TrendCharts({ trend, lang }: { trend: { month: string; total: number; verde: number; giallo: number; rosso: number; vietato: number }[]; lang: string }) {
  const { t } = useTranslation()
  const data = trend.map((p) => ({ ...p, label: monthLabel(p.month, lang) }))
  const axisColor = 'hsl(var(--muted-foreground))'
  const tooltipStyle = {
    backgroundColor: 'hsl(var(--popover))',
    border: '1px solid hsl(var(--border))',
    borderRadius: '0.5rem',
    fontSize: '12px',
    color: 'hsl(var(--popover-foreground))',
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Volume over time */}
      <Card className="it-card-accent">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-mim-blue" />
            {t('dashboard.charts.overTime')}
          </CardTitle>
        </CardHeader>
        <CardContent className="pl-0">
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={data} margin={{ top: 5, right: 15, left: -15, bottom: 0 }}>
              <defs>
                <linearGradient id="totalGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#003DA5" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#003DA5" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: axisColor }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: axisColor }} axisLine={false} tickLine={false} width={40} />
              <Tooltip contentStyle={tooltipStyle} />
              <Area
                type="monotone"
                dataKey="total"
                name={t('dashboard.charts.total')}
                stroke="#003DA5"
                strokeWidth={2}
                fill="url(#totalGrad)"
                animationDuration={900}
              />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Verdict distribution per month */}
      <Card className="it-card-accent">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-mim-blue" />
            {t('dashboard.charts.byMonth')}
          </CardTitle>
        </CardHeader>
        <CardContent className="pl-0">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data} margin={{ top: 5, right: 15, left: -15, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: axisColor }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: axisColor }} axisLine={false} tickLine={false} width={40} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'hsl(var(--muted))', opacity: 0.4 }} />
              <Legend wrapperStyle={{ fontSize: '11px' }} iconSize={9} />
              <Bar dataKey="verde" name={t('dashboard.charts.verde')} stackId="v" fill="#007A52" radius={[0, 0, 0, 0]} animationDuration={900} />
              <Bar dataKey="giallo" name={t('dashboard.charts.giallo')} stackId="v" fill="#CC7A00" animationDuration={900} />
              <Bar dataKey="rosso" name={t('dashboard.charts.rosso')} stackId="v" fill="#CC334D" animationDuration={900} />
              <Bar dataKey="vietato" name={t('dashboard.charts.vietato')} stackId="v" fill="#7A1F2E" radius={[3, 3, 0, 0]} animationDuration={900} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  )
}

function AssessmentCard({ assessment }: { assessment: AssessmentListItem }) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()

  const verdictIcon: Record<string, JSX.Element> = {
    VERDE: <CheckCircle className="w-4 h-4 text-verde" />,
    GIALLO: <AlertTriangle className="w-4 h-4 text-giallo" />,
    ROSSO: <XCircle className="w-4 h-4 text-rosso" />,
    VIETATO: <XCircle className="w-4 h-4 text-rosso-dark" />,
  }

  return (
    <div
      className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-muted/50 hover:border-mim-blue/30 cursor-pointer transition-all duration-200"
      onClick={() => navigate(`/assessment/${assessment.id}`)}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="flex-shrink-0">
          {assessment.verdict
            ? verdictIcon[assessment.verdict]
            : <Clock className="w-4 h-4 text-muted-foreground" />}
        </div>
        <div className="min-w-0">
          <p className="font-medium text-sm truncate">{assessment.app.name}</p>
          <p className="text-xs text-muted-foreground">
            {appCategoryLabel(assessment.app.category, i18n.language)} · {formatDate(assessment.created_at, i18n.language)}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
        {assessment.review_status === 'PENDING' && (
          <Badge variant="outline" className="text-xs border-giallo text-giallo-dark">
            {t('assessment.review.pendingBadge')}
          </Badge>
        )}
        {assessment.overall_score && (
          <span className="text-sm font-bold">
            {assessment.overall_score.toFixed(1)}/5
          </span>
        )}
        {assessment.verdict ? (
          <Badge className={verdictBadgeColor(assessment.verdict)}>
            {verdictLabel(assessment.verdict, i18n.language)}
          </Badge>
        ) : (
          <Badge variant="outline">
            {statusLabel(assessment.status, i18n.language)}
          </Badge>
        )}
      </div>
    </div>
  )
}

export default function Dashboard() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { data: entitlements } = useEntitlements()
  const quotaExhausted =
    !!entitlements &&
    entitlements.quota != null &&
    (entitlements.assessments_remaining ?? 0) <= 0

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['user-stats'],
    queryFn: usersApi.getStats,
  })

  const { data: assessments, isLoading: assessmentsLoading } = useQuery({
    queryKey: ['assessments', 'recent'],
    queryFn: () => assessmentsApi.list({ page: 1, page_size: 5 }),
  })

  const isReviewer = !!user && ['DPO', 'DIRIGENTE'].includes(user.role)
  const { data: pendingReviews } = useQuery({
    queryKey: ['review-queue', 'count'],
    queryFn: () =>
      assessmentsApi.list({ scope: 'school', review_status: 'PENDING', page: 1, page_size: 100 }),
    enabled: isReviewer,
  })

  // La home dell'admin di piattaforma è il pannello Piattaforma: nessun
  // tenant proprio, contenuti accessibili solo via break-glass.
  if (user?.role === 'PLATFORM_ADMIN') return <Navigate to="/admin" replace />
  // Il DPO valida le analisi, non le richiede: la sua home è il Cruscotto DPO
  if (user?.role === 'DPO') return <Navigate to="/dpo" replace />
  const isIspettore = user?.role === 'ISPETTORE'

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6 stagger">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-mim-blue dark:text-primary mb-1">
            {t('dashboard.eyebrow')}
          </p>
          <h1 className="it-page-title text-2xl font-bold">
            {t('dashboard.welcome')}, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-muted-foreground text-sm mt-2">
            {t('dashboard.title')} · ValutazIA
          </p>
        </div>
        {!isIspettore && (
          <Button
            onClick={() => navigate('/assessment/new')}
            disabled={quotaExhausted}
            title={
              quotaExhausted
                ? t(entitlements?.plan === 'FREE' ? 'quota.exhaustedFree' : 'quota.exhaustedSchool')
                : undefined
            }
            className="bg-mim-blue hover:bg-blue-800 text-white"
          >
            <PlusCircle className="w-4 h-4 mr-2" />
            {t('dashboard.newAssessment')}
          </Button>
        )}
      </div>

      <QuotaBanner />

      {/* Pending reviews (DPO/Dirigente) */}
      {isReviewer && pendingReviews && pendingReviews.length > 0 && (
        <button
          type="button"
          onClick={() => navigate('/review-queue')}
          className="w-full flex items-center gap-3 p-4 bg-giallo-light border border-giallo/40 rounded-xl text-left hover:bg-giallo-light/70 transition-colors"
        >
          <Eye className="w-5 h-5 text-giallo-dark flex-shrink-0" />
          <p className="text-sm font-medium text-giallo-dark flex-1">
            {pendingReviews.length}{pendingReviews.length === 100 ? '+' : ''}{' '}
            {t('reviewQueue.pendingCount')}
          </p>
          <ChevronRight className="w-4 h-4 text-giallo-dark flex-shrink-0" />
        </button>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statsLoading ? (
          Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)
        ) : (
          <>
            <StatCard
              title={t('dashboard.totalAssessments')}
              value={stats?.total || 0}
              icon={TrendingUp}
              color="bg-mim-blue"
            />
            <StatCard
              title={t('dashboard.stats.verde')}
              value={stats?.verde || 0}
              icon={CheckCircle}
              color="bg-verde"
            />
            <StatCard
              title={t('dashboard.stats.giallo')}
              value={stats?.giallo || 0}
              icon={AlertTriangle}
              color="bg-giallo"
            />
            <StatCard
              title={t('dashboard.stats.rosso')}
              value={stats?.rosso || 0}
              icon={XCircle}
              color="bg-rosso"
            />
          </>
        )}
      </div>

      {/* Trend charts (shown once there is at least some history) */}
      {stats?.trend && stats.trend.length > 0 && (
        <TrendCharts trend={stats.trend} lang={i18n.language} />
      )}

      {/* Recent Assessments + News */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Assessments */}
        <Card className="it-card-accent lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base">{t('dashboard.recentAssessments')}</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/history')}
              className="text-mim-blue"
            >
              {t('dashboard.viewAll')}
            </Button>
          </CardHeader>
          <CardContent>
            {assessmentsLoading ? (
              <div className="space-y-2">
                {Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-14 rounded-lg" />)}
              </div>
            ) : assessments && assessments.length > 0 ? (
              <div className="space-y-2">
                {assessments.map((a) => <AssessmentCard key={a.id} assessment={a} />)}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p className="text-sm">{t('dashboard.noAssessments')}</p>
                {!isIspettore && (
                  <Button
                    variant="link"
                    className="mt-2 text-mim-blue"
                    onClick={() => navigate('/assessment/new')}
                  >
                    {t('dashboard.newAssessment')}
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Regulatory News */}
        <Card className="it-card-accent">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Info className="w-4 h-4 text-mim-orange" />
              {t('dashboard.news.title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="border-l-2 border-mim-blue pl-3">
              <p className="text-sm font-semibold">{t('dashboard.news.aiact.title')}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {t('dashboard.news.aiact.body')}
              </p>
            </div>
            <div className="border-l-2 border-mim-orange pl-3">
              <p className="text-sm font-semibold">{t('dashboard.news.mim.title')}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {t('dashboard.news.mim.body')}
              </p>
            </div>
            <div className="text-xs text-muted-foreground mt-2 p-3 bg-muted rounded-lg">
              <p className="font-medium text-foreground mb-1">{t('dashboard.news.framework')}</p>
              <p>· Reg. (UE) 2024/1689 – AI Act</p>
              <p>· DM 166/2025 – Linee Guida MIM</p>
              <p>· Reg. (UE) 2016/679 – GDPR</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
